<section class="prereq">
  <p class="hint">
    Einen Kurs verknüpfen heißt: der Agent dieses Kurses darf dessen Inhalte
    <strong>lesen</strong> — nicht nur nennen. Wirksam wird das, sobald
    <strong>eine</strong> verantwortliche Person des verknüpften Kurses
    zustimmt. Bist du dort selbst verantwortlich, gilt es sofort.
  </p>

  <!-- Anfragen. Gesucht wird über alle Kurse des Mandanten, nicht über eine
       mitgegebene Liste: die aufrufenden Übersichten (Moodle, ILIAS) halten nur
       eine Tabellenseite, und eine Auswahl daraus bot genau diese 50 Kurse an.

       Die Liste ist bewusst kein nb-autocomplete: dessen Direktive schreibt bei
       jedem Tastendruck `handleDisplayFn` ins Feld zurück und schließt das
       Overlay nach jeder Auswahl — beides steht einer Mehrfachauswahl im Weg. -->
  <div class="prereq__request" *ngIf="canRequest">
    <div class="prereq__search-row">
      <nb-form-field class="prereq__search" #searchField cdkOverlayOrigin #searchOrigin="cdkOverlayOrigin">
        <nb-icon nbPrefix icon="search-outline"></nb-icon>
        <input
          #searchInput
          type="text"
          nbInput
          fullWidth
          autocomplete="off"
          data-enter-ignore
          role="combobox"
          aria-autocomplete="list"
          [attr.aria-expanded]="panelOpen"
          [formControl]="searchControl"
          placeholder="Kurs suchen …"
          (focus)="openPanel()"
          (click)="openPanel()"
          (keydown.escape)="closePanel()"
        />
        <nb-icon
          *ngIf="searching"
          nbSuffix
          icon="loader-outline"
          class="al-spin"
        ></nb-icon>
      </nb-form-field>
      <button
        nbButton
        size="small"
        status="primary"
        [disabled]="!picked.length || busy"
        (click)="request()"
        nbTooltip="Beantragt die Verknüpfungen und benachrichtigt die Verantwortlichen, deren Zustimmung noch fehlt"
      >
        {{ busy ? 'Läuft …' : requestLabel }}
      </button>
    </div>

    <!-- Im CDK-Overlay, nicht in der Karte: `nb-card-body` scrollt, eine
         absolut gesetzte Liste wäre dort abgeschnitten. -->
    <ng-template
      cdkConnectedOverlay
      [cdkConnectedOverlayOrigin]="searchOrigin"
      [cdkConnectedOverlayOpen]="panelOpen"
      [cdkConnectedOverlayPositions]="panelPositions"
      [cdkConnectedOverlayWidth]="panelWidth"
      (overlayOutsideClick)="closePanel()"
      (detach)="closePanel()"
    >
      <div class="prereq__panel" (scroll)="onPanelScroll($event)">
        <button
          type="button"
          class="prereq__opt"
          *ngFor="let c of visibleResults; trackBy: trackCourse"
          [class.is-selected]="isSelected(c)"
          [class.is-locked]="!isPickable(c)"
          [attr.aria-disabled]="!isPickable(c)"
          [attr.aria-selected]="isSelected(c)"
          (click)="toggle(c)"
        >
          <nb-icon
            class="prereq__opt-check"
            [icon]="isSelected(c) ? 'checkmark-square-2-outline' : 'square-outline'"
          ></nb-icon>
          <span class="prereq__opt-name">{{ c.name }}</span>
          <span class="prereq__opt-source" *ngIf="sourceLabel(c)">{{ sourceLabel(c) }}</span>
          <span class="prereq__opt-note" *ngIf="!isPickable(c)">kein Verantwortlicher</span>
        </button>

        <p class="prereq__panel-note" *ngIf="!visibleResults.length && !searching">
          Kein Kurs gefunden.
        </p>
        <p class="prereq__panel-note" *ngIf="searching && !visibleResults.length">
          Wird gesucht …
        </p>
        <p class="prereq__panel-note" *ngIf="loadingMore">Lädt weitere …</p>
      </div>
    </ng-template>

    <!-- Sagt einmal, was sonst vierzig graue Zeilen sagen würden. -->
    <p class="prereq__hint-inline" *ngIf="panelOpen && noneLinkable">
      Für keinen dieser Kurse ist jemand verantwortlich. Ohne eine
      verantwortliche Person kann niemand zustimmen — erst in „Verantwortliche
      verwalten" eintragen, dann verknüpfen.
    </p>

    <!-- Die Wahl steht als Marken unter dem Feld, nicht im Feld: man sieht,
         was man zusammengesucht hat, und kann einzeln zurücknehmen. -->
    <div class="prereq__chips" *ngIf="picked.length">
      <span class="prereq__chip" *ngFor="let c of picked">
        <span class="prereq__chip-name">{{ c.name }}</span>
        <button
          type="button"
          class="prereq__chip-remove"
          (click)="unpick(c)"
          nbTooltip="Aus der Auswahl nehmen"
        >
          <nb-icon icon="close-outline"></nb-icon>
        </button>
      </span>
    </div>

    <!-- Sagt, dass die Liste nicht alles ist. Sonst sucht niemand weiter. -->
    <p class="prereq__more" *ngIf="hasMore">
      {{ results.length }} von {{ totalMatches }} Kursen geladen — weiter
      blättern oder tippen, um zu finden, was fehlt.
    </p>
  </div>

  <p class="hint" *ngIf="!canRequest">
    Beantragen dürfen Manager, Admins und die Verantwortlichen dieses Kurses.
  </p>

  <p class="prereq__empty" *ngIf="!loading && !links.length">
    Noch keine Verknüpfungen. Der Agent bleibt strikt auf diesen Kurs beschränkt.
  </p>

  <!-- Ein Eintrag, zweimal verwendet: einmal für "dieser Kurs liest aus",
       einmal für die Gegenrichtung. Zusammengelegt, weil beide Blöcke Zeile
       für Zeile gleich waren und getrennt auseinanderlaufen würden.

       Kompakt: sichtbar bleibt eine Zeile — Kurs, Status, Aufklapper. Alles
       Weitere (Erklärung, wer wann bestätigt hat, die Knöpfe) steckt in den
       Details, weil man es erst braucht, wenn man eingreifen will. -->
  <ng-template #linkRow let-l let-other="other" let-mine="mine">
    <div class="prereq__link" [class.is-active]="l.status === 'active'" [class.is-open]="isExpanded(l)">
      <button type="button" class="prereq__link-head" (click)="toggleExpanded(l)">
        <nb-icon
          class="prereq__lock"
          [icon]="l.status === 'active' ? 'unlock-outline' : 'lock-outline'"
        ></nb-icon>
        <span class="prereq__course">{{ courseName_(other) }}</span>
        <span class="prereq__status" [class.is-live]="l.status === 'active'">{{ statusLabel(l) }}</span>
        <!-- Ruft zum Handeln auf, ohne dass man aufklappen muss. -->
        <span class="prereq__yours" *ngIf="l.my_state === 'pending'">Ihre Zustimmung fehlt</span>
        <nb-icon
          class="prereq__chevron"
          [icon]="isExpanded(l) ? 'chevron-up-outline' : 'chevron-down-outline'"
        ></nb-icon>
      </button>

      <div class="prereq__details" *ngIf="isExpanded(l)">
        <p class="prereq__hint">{{ statusHint(l) }}</p>

        <ul class="prereq__approvals">
          <li *ngFor="let a of l.approvals" [ngClass]="'is-' + a.state">
            <nb-icon
              [icon]="a.state === 'granted' ? 'checkmark-circle-2-outline'
                     : a.state === 'rejected' ? 'close-circle-outline' : 'clock-outline'"
            ></nb-icon>
            <span>{{ approvalLabel(a) }}</span>
            <span class="prereq__when" *ngIf="a.decided_at">{{ a.decided_at | date : 'short' }}</span>
            <span class="prereq__note" *ngIf="a.note">{{ a.note }}</span>
          </li>
        </ul>

        <!-- Die Knöpfe hängen an MEINER Zustimmung, nicht am Gesamtstatus.
             Vorher stand "Bestätigen" auch dann da, wenn man längst zugestimmt
             hatte — der Klick schrieb dieselbe Zustimmung erneut und im Bild
             änderte sich nichts. -->
        <div class="prereq__actions" *ngIf="l.my_state">
          <ng-container *ngIf="l.my_state === 'pending'">
            <button
              nbButton size="tiny" status="success" [disabled]="busy"
              (click)="decide(l, true)"
              [nbTooltip]="mine ? 'Ihre Zustimmung geben. Die Verknüpfung wird erst aktiv, wenn niemand mehr aussteht.' : 'Ihre Zustimmung geben — Ihr Kurs gibt damit seine Inhalte frei.'"
            >Bestätigen</button>
            <button
              nbButton size="tiny" status="basic" class="btn-caution" [disabled]="busy"
              (click)="decide(l, false)"
              nbTooltip="Ablehnen. Eine einzige Ablehnung schließt die Verknüpfung."
            >Ablehnen</button>
          </ng-container>

          <p class="prereq__waiting" *ngIf="l.my_state === 'granted' && l.status === 'pending'">
            Ihre Zustimmung liegt vor.
          </p>

          <button
            *ngIf="canOverride(l)"
            nbButton size="tiny" status="primary" [disabled]="busy"
            (click)="decide(l, true, true)"
            nbTooltip="Beantwortet alle noch offenen Zustimmungen. Wird als Entscheidung der Verwaltung protokolliert."
          >Für alle bestätigen</button>

          <button
            nbButton size="tiny" status="basic" class="btn-caution" [disabled]="busy"
            *ngIf="l.status === 'active' && l.my_state === 'granted'"
            (click)="revoke(l)"
            [nbTooltip]="mine ? 'Ihre Zustimmung zurückziehen. Der Inhaltszugriff endet sofort.' : 'Zustimmung zurückziehen. Der fremde Agent verliert den Zugriff sofort.'"
          >Widerrufen</button>
        </div>

        <p class="prereq__waiting" *ngIf="!l.my_state && !canOverride(l)">
          Sie sind für keinen der beiden Kurse verantwortlich und können hier nicht entscheiden.
        </p>
        <div class="prereq__actions" *ngIf="!l.my_state && canOverride(l)">
          <button
            nbButton size="tiny" status="primary" [disabled]="busy"
            (click)="decide(l, true, true)"
            nbTooltip="Beantwortet alle noch offenen Zustimmungen. Wird als Entscheidung der Verwaltung protokolliert."
          >Für alle bestätigen</button>
          <button
            nbButton size="tiny" status="basic" class="btn-caution" [disabled]="busy"
            (click)="decide(l, false, true)"
          >Für alle ablehnen</button>
        </div>
      </div>
    </div>
  </ng-template>

  <!-- Dieser Kurs liest aus anderen -->
  <ng-container *ngIf="incoming.length">
    <h6 class="prereq__heading">Dieser Kurs darf lesen aus</h6>
    <ng-container *ngFor="let l of incoming">
      <ng-container
        *ngTemplateOutlet="linkRow; context: { $implicit: l, other: l.source_course_id, mine: true }"
      ></ng-container>
    </ng-container>
  </ng-container>

  <!-- Andere lesen aus diesem Kurs -->
  <ng-container *ngIf="outgoing.length">
    <h6 class="prereq__heading">Andere Kurse dürfen lesen aus diesem Kurs</h6>
    <ng-container *ngFor="let l of outgoing">
      <ng-container
        *ngTemplateOutlet="linkRow; context: { $implicit: l, other: l.target_course_id, mine: false }"
      ></ng-container>
    </ng-container>
  </ng-container>
</section>
